import React from 'react';
import {
  editCustomerUserFormValues,
  useEditCustomerUserForm,
} from './hooks/useEditCustomerUserForm';
import { S } from './EditCustomerUserForm.styles';
import { Button } from '@ansvar-sakerhet/ui/src/buttons/Button';
import { CustomerUser } from '@ansvar-sakerhet/api-client';
import { useUpdateCustomerUser } from '../../../../../../../../../../../traits/api/customerUsers/useUpdateCustomerUser';
import { useReplaceCustomerUserProjects } from '../../../../../../../../../../../traits/api/customerUsers/useReplaceCustomerUserProjects';
import {
  CustomerUserDetail,
  useGetCustomerUser,
} from '../../../../../../../../../../../traits/api/customerUsers/useGetCustomerUser';
import { Input } from '../../../../../../../../../../../components/Input';
import { ProjectAccessFields } from '../ProjectAccessFields';
import { Loading } from '../../../../../../../../../../../components/Loading';

export const EditCustomerUserForm = ({
  user,
  clientId,
  onCancel,
}: {
  user: CustomerUser;
  clientId: string;
  onCancel: () => void;
}) => {
  const { data: detail, isLoading } = useGetCustomerUser(user.customerUserId);
  const projectIdsFromDetail =
    (detail as CustomerUserDetail | undefined)?.projectCustomerUser?.map(
      (p) => p.projectId
    ) ?? [];

  if (isLoading || !detail) {
    return <Loading />;
  }

  return (
    <EditCustomerUserFormInner
      user={{
        ...user,
        ...detail,
        hasAllProjectAccess: detail.hasAllProjectAccess,
      }}
      clientId={clientId}
      projectIds={projectIdsFromDetail}
      onCancel={onCancel}
    />
  );
};

const EditCustomerUserFormInner = ({
  user,
  clientId,
  projectIds,
  onCancel,
}: {
  user: CustomerUser;
  clientId: string;
  projectIds: string[];
  onCancel: () => void;
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useEditCustomerUserForm({ user, projectIds });
  const updateUser = useUpdateCustomerUser();
  const replaceProjects = useReplaceCustomerUserProjects();

  const hasAllProjectAccess = watch('hasAllProjectAccess');
  const selectedProjectIds = watch('projectIds');

  const onSubmit = async (data: editCustomerUserFormValues) => {
    await updateUser.mutateAsync({
      customerUserId: user.customerUserId,
      data: {
        firstName: data.firstName,
        lastName: data.lastName,
        hidden: data.hidden,
        hasAllProjectAccess: data.hasAllProjectAccess,
      },
    });

    if (!data.hasAllProjectAccess) {
      await replaceProjects.mutateAsync({
        customerUserId: user.customerUserId,
        data: { projectIds: data.projectIds },
      });
    }

    onCancel();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <S.Container>
        <Input
          label="Förnamn"
          error={errors.firstName}
          {...register('firstName')}
        />
        <Input
          label="Efternamn"
          error={errors.lastName}
          {...register('lastName')}
        />

        <Input
          label={'Dölj personen'}
          style={{ height: 20, float: 'left' }}
          type="checkbox"
          {...register('hidden')}
        />

        <ProjectAccessFields
          clientId={clientId}
          hasAllProjectAccess={hasAllProjectAccess}
          selectedProjectIds={selectedProjectIds}
          onHasAllProjectAccessChange={(value) =>
            setValue('hasAllProjectAccess', value, { shouldValidate: true })
          }
          onSelectedProjectIdsChange={(ids) =>
            setValue('projectIds', ids, { shouldValidate: true })
          }
        />
        {errors.projectIds && (
          <S.ErrorText>{errors.projectIds.message}</S.ErrorText>
        )}

        <S.Flex $justify="between" $gap={16} $flexDir="row">
          <Button $outlined type="button" onClick={onCancel}>
            Avbryt
          </Button>
          <Button type="submit">Spara</Button>
        </S.Flex>
      </S.Container>
    </form>
  );
};
